import Promise from 'bluebird'
import _ from 'lodash'
import React from 'react'

import { RequestMethod } from 'shared-libs/models/store'
import { Entity } from 'shared-libs/models/entity'

import apis from 'browser/app/models/apis'
import { IBaseProps } from 'browser/components/atomic-elements/atoms/base-props'
import { Classes } from '@blueprintjs/core'
import { Button } from 'browser/components/atomic-elements/atoms/button/button'

enum ProcessStatus {
  IDLE = 'idle',
  PROCESSING = 'processing',
  SUCCESS = 'success',
  FAILURE = 'failure',
}

interface IBulkEntityProcess extends IBaseProps {
  frames?: any
  entities?: any[]
  buttonText?: string
  shouldAutoRun?: boolean
  onSuccess?: () => void
  onFailure?: () => void
  onChange?: (newValue: any, silentUpdate: any) => void
  successMessage?: string
  failureMessage?: string
  noEntitiesMessage?: string
  requestMethod?: RequestMethod
}

interface IBulkEntityProcessState {
  status: ProcessStatus
  errors: { id: string; message: string }[]
  completedCount: number
}

export class BulkEntityProcess extends React.Component<
  IBulkEntityProcess,
  IBulkEntityProcessState
> {
  public static defaultProps: Partial<IBulkEntityProcess> = {
    buttonText: 'Save Entities',
    successMessage: 'All entities saved successfully',
    failureMessage: 'Some entities failed to save',
    noEntitiesMessage: 'No entities to process',
    entities: [],
    requestMethod: RequestMethod.POST,
  }

  /**
   * This component supports bulk creation, updating, and deletion of entities.
   * It accepts an array of plain objects via the entities prop and performs the specified operation
   * — POST, PUT, PATCH, or DELETE — on each one. Each object is converted into an Entity before the
   * operation is executed.
   *
   * The result of each operation is stored in the component’s internal value, allowing other components
   * to react conditionally based on success or failure. The operation can be triggered automatically based on
   * a condition or manually via a button press. If any operation fails, errors will be displayed along with
   * a Retry button.
   *
   * Example usage (jsonnet):
   *
   *   {
   *     entities: utils.expression('(facility_setup_execution.appointments).concat(facility_setup_execution.bols).concat(facility_setup_execution.trailers).concat(facility_setup_execution.spots).concat([facility_setup_execution.yardView])'),
   *     shouldAutoRun: true,
   *     requestMethod: 'PUT',
   *     successMessage: 'Entities created successfully',
   *     transientValue: '_createEntitiesResult',
   *     type: 'ui:bulkEntityProcess',
   *   }
   *   On operation completion, _createEntitiesResult in the above example will be equal to the current state of the component
   *   e.g. { status: 'success', errors: [], completedCount: 50 }
   *
   */
  constructor(props: Readonly<IBulkEntityProcess>) {
    super(props)

    this.state = {
      errors: [],
      completedCount: 0,
      status: ProcessStatus.IDLE,
    }
  }

  public componentDidMount() {
    if (this.props.shouldAutoRun) {
      this.handleProcessEntities()
    }
  }

  public componentDidUpdate() {
    if (this.props.shouldAutoRun && this.state.status === ProcessStatus.IDLE) {
      this.handleProcessEntities()
    }
  }

  public render() {
    return (
      <>
        {this.renderButton()}
        {this.renderStatus()}
        {this.renderErrors()}
      </>
    )
  }

  private renderButton() {
    const { buttonText, style, className, shouldAutoRun } = this.props
    const { status } = this.state
    if (!shouldAutoRun || status === ProcessStatus.FAILURE) {
      return (
        <Button
          className={className}
          onClick={this.handleProcessEntities}
          style={style}
          isDisabled={status === ProcessStatus.PROCESSING}
        >
          {status === ProcessStatus.IDLE && buttonText}
          {status === ProcessStatus.PROCESSING && 'Processing...'}
          {status === ProcessStatus.SUCCESS && 'Entities Processed'}
          {status === ProcessStatus.FAILURE && 'Retry'}
        </Button>
      )
    }
  }

  private renderStatus() {
    const { successMessage, entities, noEntitiesMessage, shouldAutoRun } = this.props
    const { status, completedCount } = this.state
    const total = entities.length

    if (status === ProcessStatus.SUCCESS && entities.length === 0) {
      return (
        <div style={{ marginBottom: '1em' }}>
          <p>{noEntitiesMessage}</p>
        </div>
      )
    } else if (status === ProcessStatus.SUCCESS && shouldAutoRun && entities.length > 0) {
      return (
        <div style={{ marginBottom: '1em' }}>
          <p>{successMessage}</p>
        </div>
      )
    } else {
      return (
        <div style={{ marginBottom: '1em' }}>
          <p>
            Processing entities... ({completedCount || 0} / {total})
          </p>
          <progress value={completedCount} max={total} style={{ width: '100%' }} />
        </div>
      )
    }
  }

  private renderErrors() {
    const { failureMessage } = this.props
    const { errors } = this.state
    if (errors.length > 0) {
      return (
        <div>
          {errors.map((error) => (
            <div key={error.id}>
              Error processing entity {error.id ?? '<unknown>'}: {error.message ?? '<unknown>'}
            </div>
          ))}
          <span>{failureMessage}</span>
        </div>
      )
    }
  }

  private handleProcessEntities = async () => {
    const { onSuccess, onChange, onFailure } = this.props
    let { errors } = this.state
    let status = ProcessStatus.SUCCESS

    let entities
    if (errors?.length > 0) {
      entities = this.props.entities.filter((e) => !errors.some((err) => err.id === e.uniqueId))
    } else {
      entities = this.props.entities
    }

    if (entities?.length > 0) {
      this.setState({ status: ProcessStatus.PROCESSING, errors: [], completedCount: 0 })

      const results = await Promise.map(entities, (record) => this.processEntity(record), {
        concurrency: 5,
      })

      errors = results.filter((r) => (r as any)?.error === true) as {
        id: string
        message: string
      }[]

      status = errors.length > 0 ? ProcessStatus.FAILURE : ProcessStatus.SUCCESS
    }

    this.setState(
      {
        status,
        errors,
      },
      () => {
        onChange?.(this.state, false)
        if (status === ProcessStatus.SUCCESS) {
          onSuccess?.()
        } else {
          onFailure?.()
        }
      }
    )
  }

  private processEntity(record: any): Promise<any> {
    let entity: Entity
    if (record instanceof Entity) {
      entity = record
    } else {
      entity = new Entity({}, apis)
      entity.setContent(record)
    }
    const { requestMethod } = this.props

    let processFn: () => any
    switch (requestMethod) {
      case RequestMethod.POST:
      case RequestMethod.PUT:
      case RequestMethod.PATCH:
        processFn = () => entity.save({}, requestMethod)
        break
      case RequestMethod.DELETE:
        processFn = () => entity.delete()
        break
      default:
        return Promise.reject(new Error(`Unsupported request method: ${requestMethod}`))
    }

    return processFn()
      .catch((err: any) => {
        if (requestMethod === RequestMethod.DELETE && err.status === 410) {
          // Don't throw an error for 410 Gone if request was to delete the entity anyway
          return
        }
        const message = err.message || _.values(err.errors)[0]
        return { error: true, id: record.uniqueId, message }
      })
      .finally(() => {
        this.setState((prev) => ({ completedCount: (prev.completedCount || 0) + 1 }))
      })
  }
}
